/**
 * InlineOrderItemsEditor
 * ----------------------
 * Feb 2026 — dropped into the Admin Orders card when a staff/admin
 * user with the `allow_order_inline_editing` flag (super admin
 * always) toggles "Edit items" on a non-terminal order.
 *
 * Presents a controlled form of the current items list where each
 * row lets the user:
 *   • change qty (integer spinner)
 *   • change price (decimal input) — only when `showPrice`
 *   • remove the item (X button)
 *
 * A product picker at the bottom lets the user add an item from the
 * admin product catalog (dedup happens on save — same product id
 * increments the existing row instead of adding a duplicate line).
 *
 * A "Save changes" / "Cancel" pair sits at the bottom. Save is
 * disabled until the draft is dirty. All persistence + server
 * enforcement lives in the parent (`AdminOrders.jsx`) — this
 * component is pure UI.
 *
 * Feb 2026 (later) — two visibility updates:
 *   • `showPrice` (default true) — when false, hides the Price input,
 *     line total, subtotal, and catalog price hint. Controlled by
 *     `orders.field_total` sub-admin permission upstream.
 *   • On-hand indicator: each row shows the live "In Hand" stock
 *     count derived from the placement-only availability model
 *     (Step 1 — Feb 2026). Colour-coded: red ≤0, amber <10, emerald
 *     ≥10.
 */
import React, { useMemo, useState } from 'react';
import { Trash2, Save, X, PlusCircle, Search } from 'lucide-react';
import Rial from './Rial';

const currency = (n) => (
  <><Rial /> {Number(n || 0).toFixed(3)}</>
);

// Feb 2026 (Step 1) — Branch availability is placement-only. This
// admin editor now mirrors that: "In Hand" is `available_qty`
// (physical placements minus allocated) as published by
// `/api/admin/products`. Legacy `batches[]` / `stock_qty` fallbacks
// have been removed so a product with no placements shows 0 — same
// contract as BranchNewOrder.availableQty. `available_qty` already
// clamps to ≥0 upstream; we still call Math.max as a defensive net
// against a partial payload.
const onHandFromProduct = (p) => {
  if (!p) return 0;
  return Math.max(0, Number(p.available_qty || 0));
};

const InlineOrderItemsEditor = ({
  order,
  draft,
  onQty,
  onPrice,
  onRemove,
  onAdd,
  catalog,
  onSave,
  onCancel,
  showPrice = true,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Product-id → live catalog record. Lets each row look up on-hand
  // stock in O(1) without re-scanning the catalog for every item.
  const catalogById = useMemo(() => {
    const m = {};
    for (const p of Array.isArray(catalog) ? catalog : []) {
      if (p && p.id) m[p.id] = p;
    }
    return m;
  }, [catalog]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(catalog) ? catalog : [];
    if (!q) return list.slice(0, 30);
    return list
      .filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q)
          || (p.size || '').toLowerCase().includes(q)
          || (p.sku || '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [catalog, search]);

  const subtotal = draft.items.reduce(
    (s, it) => s + Number(it.price || 0) * Number(it.qty || 0),
    0,
  );

  return (
    <div
      className="space-y-2 rounded-xl border border-[#0078D4]/40 bg-[#0078D4]/5 p-3"
      data-testid={`order-${order.id}-inline-editor`}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#0078D4]">
        Editing items · changes require branch/customer confirmation
      </div>

      {/* Item rows */}
      <div className="space-y-2">
        {draft.items.length === 0 && (
          <div className="text-xs text-neutral-500 italic py-2">
            All items removed — add at least one product before saving.
          </div>
        )}
        {draft.items.map((it, idx) => {
          const catRec = catalogById[it.id];
          const onHand = onHandFromProduct(catRec);
          const stockClass = onHand <= 0
            ? 'text-red-600'
            : onHand < 10
              ? 'text-amber-600'
              : 'text-emerald-700';
          const overLimit = Number(it.qty || 0) > onHand;
          return (
            <div
              key={`${it.id}-${idx}`}
              className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-neutral-200 p-2"
              data-testid={`order-${order.id}-edit-row-${idx}`}
            >
              <div
                className="shrink-0 w-7 text-right text-xs tabular-nums font-semibold text-neutral-400"
                data-testid={`order-${order.id}-edit-line-no-${idx}`}
                title="Line number (does not change when a line is deleted)"
              >
                #{(it?.line_no != null && it?.line_no !== '') ? it.line_no : (idx + 1)}
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-semibold text-[#0F172A]">
                  {(() => {
                    // Item Code (SKU) prefix — prefer the persisted
                    // order-item SKU (Feb 2026 rehydration), fall back
                    // to the live catalog record so legacy orders whose
                    // items still lack a stored sku still render one.
                    const sku = (it.sku || catRec?.sku || '').toString().trim();
                    return sku ? (
                      <>
                        <span
                          className="font-mono text-xs text-neutral-500 mr-1.5"
                          data-testid={`order-${order.id}-edit-sku-${idx}`}
                        >
                          {sku}
                        </span>
                        {it.name}
                      </>
                    ) : it.name;
                  })()}
                </div>
                {it.size && (
                  <div className="text-[11px] text-neutral-500">{it.size}</div>
                )}
                <div
                  className={`text-[11px] font-semibold tabular-nums mt-0.5 ${stockClass}`}
                  data-testid={`order-${order.id}-edit-onhand-${idx}`}
                  title="Current on-hand stock (from inventory)"
                >
                  In Hand: {onHand} <span className="text-neutral-400 font-normal">{it.uom || 'ctn'}</span>
                  {overLimit && (
                    <span
                      className="ml-2 text-red-600"
                      data-testid={`order-${order.id}-edit-onhand-warn-${idx}`}
                    >
                      · exceeds stock
                    </span>
                  )}
                </div>
              </div>
              <label className="flex items-center gap-1 text-[11px] text-neutral-600">
                Qty
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={it.qty}
                  onChange={(e) => onQty(idx, Math.max(1, parseInt(e.target.value || '1', 10)))}
                  className="w-20 px-2 py-1 text-sm rounded border border-neutral-300 focus:border-[#0078D4] focus:outline-none"
                  data-testid={`order-${order.id}-edit-qty-${idx}`}
                />
                <span className="ml-1 text-neutral-400">{it.uom || 'ctn'}</span>
              </label>
              {showPrice && (
                <label className="flex items-center gap-1 text-[11px] text-neutral-600">
                  Price
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={it.price}
                    onChange={(e) => onPrice(idx, Math.max(0, parseFloat(e.target.value || '0')))}
                    className="w-24 px-2 py-1 text-sm rounded border border-neutral-300 focus:border-[#0078D4] focus:outline-none"
                    data-testid={`order-${order.id}-edit-price-${idx}`}
                  />
                  <span className="ml-0.5 text-neutral-400"><Rial /></span>
                </label>
              )}
              {showPrice && (
                <div className="text-xs font-semibold text-neutral-700 min-w-[80px] text-right">
                  {currency(Number(it.price || 0) * Number(it.qty || 0))}
                </div>
              )}
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="p-1.5 rounded-full text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200"
                title="Remove item"
                data-testid={`order-${order.id}-edit-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Add-item picker */}
      <div className="pt-2">
        {!pickerOpen ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-3 py-1 font-bold"
            data-testid={`order-${order.id}-edit-add-open`}
          >
            <PlusCircle className="w-3.5 h-3.5" /> Add item
          </button>
        ) : (
          <div className="bg-white rounded-lg border border-neutral-200 p-2 space-y-2">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-neutral-400" />
              <input
                type="text"
                autoFocus
                placeholder="Search products by name or item code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-sm px-2 py-1 border-b border-neutral-200 focus:border-[#0078D4] focus:outline-none"
                data-testid={`order-${order.id}-edit-add-search`}
              />
              <button
                type="button"
                onClick={() => { setPickerOpen(false); setSearch(''); }}
                className="p-1 rounded-full hover:bg-neutral-100"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 text-sm">
              {filteredProducts.length === 0 && (
                <div className="text-xs text-neutral-500 italic py-2">No products match.</div>
              )}
              {filteredProducts.map((p) => {
                const pickOnHand = onHandFromProduct(p);
                const pickClass = pickOnHand <= 0
                  ? 'text-red-600'
                  : pickOnHand < 10
                    ? 'text-amber-600'
                    : 'text-emerald-700';
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onAdd(p); setSearch(''); }}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-[#0078D4]/10 flex items-center gap-2"
                    data-testid={`order-${order.id}-edit-add-pick-${p.id}`}
                  >
                    <span className="flex-1 min-w-0 truncate">
                      {p.sku && (
                        <span
                          className="font-mono text-xs text-neutral-500 mr-1.5"
                          data-testid={`order-${order.id}-edit-add-pick-sku-${p.id}`}
                        >
                          {p.sku}
                        </span>
                      )}
                      {p.name}
                      {p.size && <span className="text-neutral-500 text-xs ml-1">({p.size})</span>}
                    </span>
                    <span className={`text-[11px] font-semibold tabular-nums ${pickClass}`}>
                      {pickOnHand} {p.uom || 'ctn'}
                    </span>
                    {showPrice && (
                      <span className="text-xs text-neutral-500">{currency(p.price)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Live totals + Save/Cancel */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#0078D4]/20">
        {showPrice && (
          <span className="text-xs text-neutral-500">
            Subtotal: <span className="font-bold text-[#0F172A]">{currency(subtotal)}</span>
            <span className="text-neutral-400 text-[10px] ml-2">(server recomputes VAT + discount on save)</span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs rounded-full border border-neutral-300 text-neutral-700 hover:bg-neutral-50 px-3 py-1.5 font-bold inline-flex items-center gap-1"
            data-testid={`order-${order.id}-edit-cancel`}
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!draft.dirty || draft.saving}
            className="text-xs rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-50 text-white px-3 py-1.5 font-bold inline-flex items-center gap-1"
            data-testid={`order-${order.id}-edit-save`}
          >
            <Save className="w-3.5 h-3.5" />
            {draft.saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineOrderItemsEditor;
